/**
 * Pricing Utility Functions
 * 
 * Collection of utility functions for pricing calculations, validation,
 * and data processing used across the pricing system.
 * 
 * @author Wildlife Project Team
 * @version 2.1.0
 */

import { type OptionType } from '../types';
import { PEOPLE_KEYWORDS, DAYS_KEYWORDS, DEFAULT_VALUES, FormValues, PricingRule } from './pricing-types';

/**
 * Auto-detect number input values based on label keywords
 * 
 * @param options - Array of adventure options
 * @param formValues - Current form values
 * @param keywords - Keywords to search for in option labels
 * @returns Detected number value or null if not found
 */
export function autoDetectNumberValue(
  options: OptionType[],
  formValues: FormValues,
  keywords: readonly string[]
): number | null {
  for (const option of options) {
    if (option.type === 'number') {
      const label = option.label.toLowerCase();
      if (keywords.some(keyword => label.includes(keyword))) {
        const value = formValues[option.label];
        return typeof value === 'number' ? value : parseInt(value) || 0;
      }
    }
  }
  return null;
}

/**
 * Auto-detect people value from form inputs
 * 
 * @param options - Array of adventure options
 * @param formValues - Current form values
 * @returns Number of people (defaults to 1)
 */
export function detectPeopleValue(options: OptionType[], formValues: FormValues): number {
  return autoDetectNumberValue(options, formValues, PEOPLE_KEYWORDS) || DEFAULT_VALUES.PEOPLE;
}

/**
 * Auto-detect days value from form inputs
 * 
 * @param options - Array of adventure options
 * @param formValues - Current form values
 * @returns Number of days (defaults to 1)
 */
export function detectDaysValue(options: OptionType[], formValues: FormValues): number {
  return autoDetectNumberValue(options, formValues, DAYS_KEYWORDS) || DEFAULT_VALUES.DAYS;
}

/**
 * Get legacy multipliers from form values (backward compatibility)
 * 
 * @param options - Array of adventure options
 * @param formValues - Current form values
 * @returns Record of multiplier values by target
 */
export function getLegacyMultipliers(options: OptionType[], formValues: FormValues): Record<string, number> {
  const multipliers: Record<string, number> = {};
  
  options.forEach(option => {
    if (option.isMultiplier && option.multiplierTarget) {
      const value = formValues[option.label];
      if (typeof value === 'number' && value > 0) {
        multipliers[option.multiplierTarget] = value;
      }
    }
  });
  
  return multipliers;
}

/**
 * Calculate pricing value for a given pricing rule
 * 
 * @param pricing - The pricing rule to apply
 * @param usedBasePrice - Base price to use for calculations (could be scaled)
 * @param quantity - Quantity or value from form input
 * @param peopleValue - Number of people
 * @param daysValue - Number of days
 * @param multiplier - Additional multiplier (default: 1)
 * @returns Calculated pricing amount
 */
export function calculatePricingValue(
  pricing: PricingRule,
  usedBasePrice: number,
  quantity: number,
  peopleValue: number,
  daysValue: number,
  multiplier: number = 1
): number {
  switch (pricing.type) {
    case 'flat':
    case 'fixed':
      return pricing.value * multiplier;
    case 'percentage':
      return (usedBasePrice * pricing.value / 100) * multiplier;
    case 'per_unit':
      // Legacy per_unit with multiplierType
      if (pricing.multiplierType) {
        switch (pricing.multiplierType) {
          case 'people':
            return pricing.value * peopleValue;
          case 'days':
            return pricing.value * daysValue;
          case 'both':
            return pricing.value * peopleValue * daysValue;
          default:
            return pricing.value * quantity * multiplier;
        }
      }
      return pricing.value * quantity * multiplier;
    case 'per_person':
      return pricing.value * peopleValue;
    case 'per_day':
      return pricing.value * daysValue;
    case 'per_person_day':
      return pricing.value * peopleValue * daysValue;
    default:
      return 0;
  }
}

/**
 * Round price to 2 decimal places
 * 
 * @param price - Price to round
 * @returns Rounded price
 */
export function roundPrice(price: number): number {
  return Math.round(price * 100) / 100;
}

/**
 * Format price with currency symbol
 * 
 * @param price - Price to format
 * @param currencySymbol - Currency symbol
 * @returns Formatted price string
 */
export function formatPrice(price: number, currencySymbol: string): string {
  return `${currencySymbol}${roundPrice(price).toFixed(2)}`;
}

/**
 * Validate form value for pricing calculations
 * 
 * @param value - Value to validate
 * @param optionType - Type of the option ('text', 'number', 'checkbox', 'select')
 * @returns Whether the value should be included in pricing calculations
 */
export function shouldIncludeInPricing(value: any, optionType: string): boolean {
  switch (optionType) {
    case 'checkbox':
      return value === true;
    case 'select':
      return value && value !== '';
    case 'number':
      return typeof value === 'number' && value > 0;
    case 'text':
      return value && value.trim() !== '';
    default:
      return Boolean(value);
  }
}

/**
 * Generate unique ID for breakdown items
 * 
 * @param prefix - Prefix for the ID
 * @param identifier - Unique identifier
 * @param index - Optional index for multiple items
 * @returns Unique ID string
 */
export function generateBreakdownId(prefix: string, identifier: string, index?: number): string {
  const baseId = `${prefix}-${identifier}`;
  return index !== undefined ? `${baseId}-${index}` : baseId;
} 